<script setup lang="ts">
// Modal de saque (ENG-868). Casca slide-up (abre de baixo pra cima no mobile e
// card centralizado no desktop), substituindo o saque que era renderizado
// dentro do <BaseModal> genérico por components/withdraw/index.vue.
//
// O conteúdo das etapas é ÚNICO: `isMobile` muda só o casco (posição, largura,
// padding), como no modal de depósito (ENG-361). Os frames desktop do Figma
// servem de conferência de proporção, não de segundo desenho.

import type { WithdrawAccountSelection } from './StepAccount.vue';
// Import EXPLÍCITO: composables/apostatudo/* não são auto-importados pelo Nuxt
// (lint não acusa, mas quebra em runtime).
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';

const { $containment } = useNuxtApp();
const { isMobile } = useLayout();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { currentWithdrawMethod } = storeToRefs(useWalletStore());
const { selectWithdrawMethod } = useWalletStore();
const { allWithdrawMethods, defaultSelectedWithdrawMethod } = usePayments();
const { updateWallet } = useWallet();
const { gaClientId } = useParameters();
const { getMinValueToTriggerModule } = useModulesValidation();
const { onContactValidationComplete } = useAuthStore();
const { withdrawEvent } = useUserJourney();
const { fetchUserProfile } = useUserData();

const {
  isOpen,
  step,
  screen,
  isBusy,
  close,
  forceClose,
  goToStep,
  goToConfirm,
  goToSuccess,
  toggleRules,
} = useWithdrawModal();

// Aviso de perda de bônus: continua sendo um passo ANTES do formulário, como
// no fluxo antigo — enquanto ele estiver ativo, o modal de saque não aparece.
const { shouldShowBonusLossAlert } = useWithdrawBonusLossAlert();

// Conta/chave escolhida na Etapa 1. Vive aqui (orquestrador) porque as etapas
// seguintes a consomem: a Etapa 2 pra montar o resumo e a confirmação pra
// montar o payload do POST /new-withdraws.
const accountSelection = ref<WithdrawAccountSelection>({});

// Valor escolhido na Etapa 2, em centavos (unidade do core).
const amountCents = ref<number>(0);

function onAccountSubmit(selection: WithdrawAccountSelection) {
  accountSelection.value = selection;

  goToStep(2);
}

function onAmountSubmit(amount: number) {
  amountCents.value = amount;

  goToConfirm();
}

// CANCELAR volta pra Etapa 2 (não fecha o modal); "Alterar informações" volta
// pra Etapa 1, onde a chave é escolhida.
function onConfirmCancel() {
  goToStep(2);
}

function onChangeInfo() {
  goToStep(1);
}

// --- Confirmação: POST /new-withdraws --------------------------------------
// Portado de components/withdraw/index.vue sem mudança de comportamento: os
// mesmos gatilhos de containment/KYC, a mesma montagem de params e os mesmos
// efeitos pós-sucesso. O que muda é só de onde vêm os dados (pixSelection e
// accountSelection e amountCents, das etapas novas) e o `isBusy`, que trava o
// fechamento do modal enquanto a requisição está em voo.

async function onConfirm() {
  // Requisição em voo: o SIM também fica desabilitado no StepConfirm, mas o
  // guarda aqui é o que impede um duplo toque de virar dois saques.
  if (isBusy.value) return;

  if (!amountCents.value || !currentWithdrawMethod.value) return;

  // Erro de chave detectado pelo PixKeys (telefone sem DDI, chave vazia…):
  // mensagem específica, mesma do fluxo antigo.
  if (accountSelection.value.pixKeyError) {
    openToaster({ message: accountSelection.value.pixKeyError, type: 'error' });

    return;
  }

  onContactValidationComplete(() => {
    requestWithdraw();
  });

  const needsContainment =
    isPixWithdrawMethod(currentWithdrawMethod.value) &&
    amountCents.value >= getMinValueToTriggerModule('withdraw');

  if (needsContainment) {
    await $containment.handle('withdraw', () => {
      requestWithdraw();
    });

    return;
  }

  requestWithdraw();
}

function buildWithdrawParams(): Record<string, unknown> {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();
  const selection = accountSelection.value;

  const params: Record<string, unknown> = {
    amount: amountCents.value,
    method: currentWithdrawMethod.value,
  };

  // Conta bancária cadastrada tem precedência sobre o par tipo+valor da chave —
  // é o que o core espera (bank_id OU pix_key_*).
  if (selection.selectedBankAccountId) {
    params.bank_id = selection.selectedBankAccountId;
  } else if (selection.selectedKeyType && selection.selectedKeyValue) {
    params.pix_key_type = selection.selectedKeyType;
    params.pix_key_value = selection.selectedKeyValue;
  }

  if (selection.selectedPhoneNumber && selection.selectedPhoneDdi) {
    params.phone_number = selection.selectedPhoneNumber;
    params.phone_ddi = selection.selectedPhoneDdi;
  }

  if (selection.selectedBankType && selection.selectedBankNumber) {
    params.spei_bank_type = selection.selectedBankType;
    params.spei_bank_number = selection.selectedBankNumber;

    if (selection.selectedBankCode) {
      params.spei_bank_code = selection.selectedBankCode;
    }
  }

  if (gaClientId.value) params.ga_client_id = gaClientId.value;

  if ($kyc?.current?.id) params.kyc_id = $kyc.current.id;

  if (temporaryPassword.get()) params.password_check = temporaryPassword.get();

  return params;
}

async function requestWithdraw() {
  // Guarda de reentrada. Este ponto é alcançado por DOIS caminhos diferidos —
  // o callback de `onContactValidationComplete` e o de `$containment.handle` —
  // e o handler do store só é limpo quando executa, então uma validação de
  // contato de QUALQUER outro fluxo, mais tarde, cairia aqui. Sem isto, quem
  // abandonava o sheet e validava o contato depois tinha um saque postado sem
  // pedir, com o valor da tentativa anterior.
  if (!isOpen.value || !amountCents.value || !currentWithdrawMethod.value) {
    return;
  }

  const { temporaryPassword } = useModulesValidation();
  const { ApiService } = useApiService();

  isBusy.value = true;

  await ApiService.post('/new-withdraws', buildWithdrawParams())
    .then((res) => {
      if (res.data.success) {
        // Saque solicitado com sucesso → evento withdraw pro /collector. O core
        // casa por valor EXATO contra a v_external_saques (em reais); o front
        // usa centavos, então /100 pra bater.
        withdrawEvent(amountCents.value / 100);

        openToaster({
          message:
            res.data.message ||
            t('account.wallet.withdraw_modal.withdraw_success'),
          type: 'success',
        });

        goToSuccess();

        setTimeout(async () => {
          await updateWallet();
          await fetchUserProfile({ force: true });
        }, 1500);

        return;
      }

      openToaster({ message: res.data.message, type: 'error' });
    })
    .catch((error) => {
      const message =
        error.data?.message ||
        error.message ||
        error.exception ||
        error.statusText ||
        error ||
        t('account.wallet.withdraw_modal.server_error');

      openToaster({ message, type: 'error' });
    })
    .finally(() => {
      temporaryPassword.clear();
      isBusy.value = false;
    });
}

// Garante um método de saque válido. A lista vem de /payments (async) e o
// store nasce em 'pix', então sem isto um mercado sem Pix (MX: spei/oxxo)
// abriria a Etapa 1 pedindo chave Pix. Fica no casco, e não nas etapas, porque
// os chips não aparecem em todas elas.
function ensureValidMethod(methods = allWithdrawMethods.value) {
  if (!methods.length) return;

  const isValid = methods.some((m) => m.slug === currentWithdrawMethod.value);

  if (!isValid) selectWithdrawMethod(defaultSelectedWithdrawMethod.value);
}

// Cada abertura começa limpa: sem isso a chave e o valor da sessão anterior
// reapareceriam numa nova solicitação. Limpar TAMBÉM ao fechar é o que tira o
// combustível dos callbacks diferidos (ver a guarda em requestWithdraw).
//
// `immediate: true` porque este componente é chunk async: se o modal já estiver
// aberto quando o chunk resolve, o watch nunca dispara e o `ensureValidMethod`
// não roda — um jogador MX abriria a Etapa 1 pedindo chave Pix e mandaria
// `method: 'pix'`. Mesmo cuidado do modal de depósito.
watch(
  isOpen,
  (open) => {
    accountSelection.value = {};
    amountCents.value = 0;

    if (open) ensureValidMethod();
  },
  { immediate: true },
);

watch(allWithdrawMethods, (methods) => {
  if (isOpen.value) ensureValidMethod(methods);
});

// Trocar de gateway para outra FAMÍLIA invalida o que foi preenchido na Etapa
// 1: os campos são outros (chave Pix x conta mexicana x telefone). Sem isto
// dava pra preencher conta MX, voltar, escolher AstroPay e mandar
// `method: 'astropay'` junto com `spei_bank_*`.
watch(currentWithdrawMethod, (next, previous) => {
  if (!isOpen.value || !previous) return;

  if (withdrawMethodFamily(next) === withdrawMethodFamily(previous)) return;

  accountSelection.value = {};
  goToStep(1);
});

// Trava o scroll do body enquanto o modal está aberto. Sem isso o
// pull-to-refresh nativo do app recarrega a página por cima do modal.
useBodyScrollLock(isOpen);

// Título do topbar por tela (Figma). Ao contrário do depósito, aqui ele muda a
// cada etapa em vez de ser constante.
const title = computed(() => {
  const prefix = 'account.wallet.withdraw_modal';

  if (screen.value === 'rules') return t(`${prefix}.withdraw_rules`);
  if (screen.value === 'success') return t(`${prefix}.success_title`);
  if (screen.value === 'confirm') return t(`${prefix}.details_title`);

  if (step.value === 2) return t(`${prefix}.step_amount_title`);

  // "Escolher a chave pix" só descreve a Etapa 1 no Brasil; nos demais países
  // o dado pedido é outro (conta bancária, telefone).
  return isPixWithdrawMethod(currentWithdrawMethod.value)
    ? t(`${prefix}.step_pix_key_title`)
    : t(`${prefix}.step_account_title`);
});

// Voltar: existe na Etapa 2 e na tela de detalhes. O Figma só desenha o X, mas
// sem isso o único caminho de volta é fechar o modal e recomeçar.
const canGoBack = computed(
  () =>
    (screen.value === 'steps' && step.value === 2) ||
    screen.value === 'confirm' ||
    screen.value === 'rules',
);

function onBack() {
  if (screen.value === 'rules') {
    toggleRules();

    return;
  }

  goToStep(screen.value === 'confirm' ? 2 : 1);
}

// Fechar clicando fora: só quando o gesto COMEÇOU no overlay (mesmo cuidado do
// depósito) — arrastar de dentro do sheet pra fora não fecha. O close() ignora
// o pedido se houver requisição em voo.
const overlayPressed = ref(false);

function onOverlayPointerDown() {
  overlayPressed.value = true;
}

function onOverlayClick() {
  if (overlayPressed.value) close();
  overlayPressed.value = false;
}

// ESC passa pelo mesmo close() (e portanto pelo mesmo guarda de requisição em
// voo) que o X e o clique fora.
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) close();
}

// Logout com o modal aberto (ex.: 401 no meio do fluxo): este singleton não é
// filho de nada que desmonte, então o sheet ficaria sobre a página deslogada,
// com os dados da sessão anterior e o scroll do body ainda travado. Usa
// `forceClose` porque o close() normal ignora o pedido quando há requisição em
// voo — e é justamente aí que o 401 acontece.
const { isAuthenticated } = storeToRefs(useAuthStore());

watch(isAuthenticated, (authenticated) => {
  if (!authenticated && isOpen.value) forceClose();
});

onMounted(() => {
  window.addEventListener('keydown', onKeydown);

  onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown);
  });
});
</script>

<template>
  <!-- Shell próprio (Teleport + bottom-sheet). Enquanto ele estiver ativo o
       modal de saque nem aparece: confirmar a perda do bônus é pré-requisito
       pra sacar, como já era no fluxo antigo. -->
  <WithdrawBonusLossAlert v-if="isOpen && shouldShowBonusLossAlert" />

  <Teleport to="body">
    <Transition
      appear
      :duration="300"
      name="wdm"
    >
      <div
        v-if="isOpen && !shouldShowBonusLossAlert"
        class="wdm-overlay"
        :class="{
          'is-desktop': !isMobile,
          'has-pendency': $containment.isVerificationModalOpen,
        }"
        @click.self="onOverlayClick"
        @pointerdown.self="onOverlayPointerDown"
      >
        <div
          class="wdm-sheet"
          :class="{ 'is-desktop': !isMobile }"
          @dragstart.prevent
        >
          <!-- Topbar -->
          <header class="wdm-topbar">
            <button
              v-if="canGoBack"
              aria-label="Voltar"
              class="wdm-back"
              type="button"
              @click="onBack"
            >
              <NuxtIcon name="arrows/arrow_left" />
            </button>

            <span class="wdm-title">{{ title }}</span>

            <!-- Regras só nas etapas: o toggle volta pra 'steps', então
                 abri-las a partir da confirmação ou do sucesso jogaria o
                 jogador de volta ao formulário. -->
            <button
              v-if="screen === 'steps' || screen === 'rules'"
              :aria-label="$t('account.wallet.withdraw_modal.withdraw_rules')"
              class="wdm-rules-btn"
              type="button"
              @click="toggleRules"
            >
              <NuxtIcon name="info" />
            </button>

            <button
              aria-label="Fechar"
              class="wdm-close"
              type="button"
              @click="close"
            >
              <svg
                fill="none"
                height="10"
                viewBox="0 0 14 14"
                width="10"
              >
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="2"
                />
              </svg>
            </button>
          </header>

          <!-- Barra de progresso: só nas etapas. Detalhes e Sucesso são saídas
               do fluxo, não passos dele. -->
          <WithdrawModalProgressBar
            v-if="screen === 'steps'"
            :current="step"
          />

          <div class="wdm-content">
            <WithdrawRules v-if="screen === 'rules'" />

            <template v-else-if="screen === 'steps'">
              <WithdrawModalStepAccount
                v-if="step === 1"
                @submit="onAccountSubmit"
              />

              <WithdrawModalStepAmount
                v-else
                :initial-amount-cents="amountCents"
                @submit="onAmountSubmit"
              />
            </template>

            <template v-else-if="screen === 'confirm'">
              <WithdrawModalStepConfirm
                :amount-cents="amountCents"
                :selection="accountSelection"
                @cancel="onConfirmCancel"
                @change-info="onChangeInfo"
                @confirm="onConfirm"
              />
            </template>

            <template v-else-if="screen === 'success'">
              <WithdrawModalStepSuccess @close="close" />
            </template>
          </div>

          <!-- Compliance oficial no saque (ENG-404): advertência + 18+ +
               identificação, padronizado com o resto do site. -->
          <BaseComplianceInfo />

          <div class="wdm-safe-bottom" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
// Transição única (v-if só no overlay): só o .wdm-sheet desliza, via
// `animation` (keyframes). NÃO usar `transition`: o reset global em main.scss
// tem `*:not(.transition){ transition: none !important }`, que mata transition
// no app. O backdrop NÃO faz fade de propósito — animá-lo junto com o
// `backdrop-filter: blur` full-screen recompõe o blur a cada frame (caro em
// mobile fraco). O Vue não auto-detecta duração no overlay (ele não tem
// animation), então :duration="300" no <Transition> diz o tempo explícito.

.wdm-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000; // acima do header da game-bar (z-[99999])
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: rgba(2, 7, 22, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  // Desktop: card centralizado vertical e horizontalmente.
  &.is-desktop {
    justify-content: center;
    align-items: center;
  }

  // O saque dispara verificação (KYC, documento, e-mail) com frequência —
  // 'withdraw' e 'first_withdraw'. Sem rebaixar o overlay, o modal de pendência
  // abre ATRÁS deste.
  &.has-pendency {
    z-index: 996 !important;
  }
}

.wdm-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-height: 92dvh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;
  padding: 16px 16px 24px;
  background: #050c1f;
  // Bloqueia seleção de texto e menu de toque dentro do modal.
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  // Mobile: colado ao rodapé, arredondado só no topo.
  border-radius: 20px 20px 0 0;
  will-change: transform, opacity;

  // Impede arrastar imagens/links (webkit); o @dragstart.prevent cobre o resto.
  :deep(img),
  :deep(a) {
    -webkit-user-drag: none;
  }

  // Desktop: mesmo conteúdo, card flutuante centralizado.
  &.is-desktop {
    width: 512px;
    max-width: 95vw;
    padding: 24px;
    border-radius: 16px;
    max-height: 88dvh;
    box-shadow:
      0 2px 8px 0 rgba(0, 0, 0, 0.06),
      0 -6px 12px 0 rgba(0, 0, 0, 0.03),
      0 14px 28px 0 rgba(0, 0, 0, 0.08),
      inset 0 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}

// Slide do sheet via keyframe global slide-up-spring, atrelado ao enter/leave
// do overlay. Declarado depois de .wdm-sheet por causa do
// no-descending-specificity do stylelint.
.wdm-enter-active .wdm-sheet {
  animation: slide-up-spring 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

// `forwards` segura o último frame (fora da tela) até o Vue remover; sem ele o
// sheet volta ao estado aberto por 1-2 frames antes da remoção (flash).
.wdm-leave-active .wdm-sheet {
  animation: slide-up-spring 0.3s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
}

.wdm-topbar {
  display: flex;
  align-items: center;
  gap: 8px;

  .wdm-title {
    color: #fcfcfc;
    font-weight: 700;
    font-size: 15px;
    line-height: 20px;
  }

  .wdm-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;

    // NuxtIcon ignora text-[Npx]: o svg é dimensionado aqui.
    :deep(svg) {
      width: 16px;
      height: 16px;
    }
  }

  // Empurrado pra direita: o título fica encostado no voltar quando ele existe.
  .wdm-rules-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    background: rgba(255, 255, 255, 0.05);

    // NuxtIcon ignora text-[Npx]: dimensiona pelo svg.
    :deep(svg) {
      width: 14px;
      height: 14px;
    }
  }

  // Com o botão de regras no meio, quem empurra pra direita é ele; sem ele, o
  // próprio X (margin-left: auto abaixo) faz o papel.
  .wdm-rules-btn + .wdm-close {
    margin-left: 0;
  }

  .wdm-close {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.05);

    &:hover {
      background: rgba(255, 255, 255, 0.16);
    }
  }
}

.wdm-content {
  position: relative;
  display: flex;
  flex-direction: column;
}

.wdm-safe-bottom {
  height: env(safe-area-inset-bottom, 0px);
}
</style>
